import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { Lock, Check } from 'lucide-react';
import { PartnerModal } from '@/components/PartnerModal';
import { PartnerInquiryModal } from '@/components/PartnerInquiryModal';
import { HealthBenefitModal } from '@/components/HealthBenefitModal';
import { PartnerSlotWithPartner } from '@/hooks/usePartnerSlots';
import { track } from '@/lib/telemetry';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface RewardMilestoneProps {
  days: number;
  translationKey: string;
  longestStreak: number;
  currentStreak: number;
  isNextMilestone?: boolean;
  partnerSlot?: PartnerSlotWithPartner;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

// Helper to format milestone days with 💯 for 100
const formatMilestoneDays = (days: number): string => {
  return days === 100 ? '💯' : String(days);
};

export function RewardMilestone({ 
  days, 
  translationKey, 
  longestStreak,
  currentStreak,
  isNextMilestone,
  partnerSlot,
  onModalOpen,
  onModalClose,
}: RewardMilestoneProps) {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  
  const isUnlocked = longestStreak >= days;
  const hasPartner = isUnlocked && partnerSlot;

  // Fire milestone_unlocked once per session per milestone
  useEffect(() => {
    if (!isUnlocked) return;
    const storageKey = `milestone_tracked_${days}`;
    const emailKey = `milestone_email_sent_${days}`;
    try {
      if (sessionStorage.getItem(storageKey)) return;
      sessionStorage.setItem(storageKey, '1');
      track('milestone_unlocked', { milestone_days: days });
    } catch {
      track('milestone_unlocked', { milestone_days: days });
    }

    // Fire milestone email once per session per milestone — never break UI
    try {
      if (sessionStorage.getItem(emailKey)) return;
      const userEmail = user?.email;
      if (!userEmail) return;
      sessionStorage.setItem(emailKey, '1');
      const userName = profile?.user_id || 'Hráč';
      void supabase.functions
        .invoke('send-milestone-email', {
          body: { userEmail, userName, milestoneDays: days },
        })
        .catch(() => { /* silent — email is background */ });
    } catch {
      /* swallow */
    }
  }, [isUnlocked, days, user?.email, profile?.user_id]);

  // Notify parent when any modal opens/closes
  const anyModalOpen = showPartnerModal || showInquiryModal || showHealthModal;
  
  useEffect(() => {
    if (anyModalOpen) {
      onModalOpen?.();
    } else {
      onModalClose?.();
    }
  }, [anyModalOpen, onModalOpen, onModalClose]);

  // Handle opening reward modal from health benefit modal
  const handleShowReward = useCallback(() => {
    setShowHealthModal(false);
    // Small delay to ensure smooth modal transition
    setTimeout(() => {
      if (partnerSlot) {
        setShowPartnerModal(true);
      } else {
        setShowInquiryModal(true);
      }
    }, 100);
  }, [partnerSlot]);

  // Calculate progress percentage for next milestone
  const progressPercent = isNextMilestone ? Math.min((currentStreak / days) * 100, 100) : 0;

  const getCardClasses = () => {
    if (isUnlocked) {
      return 'border-primary/50 bg-primary/5';
    }
    if (isNextMilestone) {
      return 'border-border bg-card';
    }
    return 'opacity-40';
  };

  return (
    <>
      <Card className={`transition-colors overflow-hidden ${getCardClasses()}`}>
        <CardContent className="p-0 flex items-stretch">
          {/* LEFT SIDE: Milestone info */}
          <div className="flex-1 min-w-0 p-4 pr-3">
            <h3 className={`font-semibold ${
              isUnlocked ? 'text-foreground' : isNextMilestone ? 'text-foreground' : 'text-muted-foreground'
            }`}>
              {t(translationKey).replace('100', formatMilestoneDays(days))}
            </h3>
            
            {/* Secondary link */}
            <button
              onClick={() => setShowHealthModal(true)}
              className="text-sm text-muted-foreground hover:text-foreground text-left mt-1"
            >
              {isUnlocked ? (
                <>
                  <span className="underline underline-offset-2">{t('rewardHealthBenefitLinkText')}</span>
                  <span> 🎈</span>
                </>
              ) : (
                <span className="underline underline-offset-2">{t('rewardLocked')}</span>
              )}
            </button>
          </div>
          
          {/* RIGHT SIDE: Unlocked CTA / Next milestone progress / Lock */}
          {isUnlocked ? (
            // Unlocked: Show reward CTA
            <button
              onClick={() => hasPartner ? setShowPartnerModal(true) : setShowInquiryModal(true)}
              className="flex flex-col items-center justify-center w-28 shrink-0 bg-primary hover:bg-primary/80 transition-colors rounded-r-lg px-2 py-3 text-center"
            >
              <Check className="h-5 w-5 text-primary-foreground mb-1" />
              <span className="text-xs font-medium text-primary-foreground leading-tight">
                {t('rewardUnlocked')}
              </span>
            </button>
          ) : isNextMilestone ? (
            // Next milestone: Show progress bar with lock
            <div className="relative w-28 shrink-0 overflow-hidden rounded-r-lg">
              {/* Progress fill from left */}
              <div 
                className="absolute inset-y-0 left-0 bg-primary/60 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
              {/* Remaining unfilled area */}
              <div 
                className="absolute inset-y-0 right-0 bg-muted"
                style={{ width: `${100 - progressPercent}%` }}
              />
              {/* Lock icon centered in unfilled area */}
              <div className="relative h-full flex items-center justify-center">
                <Lock className="h-5 w-5 text-muted-foreground z-10" />
              </div>
            </div>
          ) : (
            // Future milestone: Just lock
            <div className="flex items-center justify-center w-28 shrink-0 bg-muted/50 rounded-r-lg">
              <Lock className="h-5 w-5 text-muted-foreground/50" />
            </div>
          )}
        </CardContent>
      </Card>

      {hasPartner && showPartnerModal && (
        <PartnerModal
          partner={partnerSlot.partner}
          milestoneDays={days}
          onClose={() => setShowPartnerModal(false)}
        />
      )}

      {showInquiryModal && (
        <PartnerInquiryModal onClose={() => setShowInquiryModal(false)} />
      )}

      {showHealthModal && (
        <HealthBenefitModal
          milestoneDays={days}
          isUnlocked={isUnlocked}
          onClose={() => setShowHealthModal(false)}
          onShowReward={isUnlocked ? handleShowReward : undefined}
        />
      )}
    </>
  );
}
